/**
 * `ChipHelp` — the hover/focus explanation behind a `#uc1Chip-*` or `#uc2Chip-*` chip.
 *
 * The chips are the one control in the pane that changes what the map paints, and their
 * labels are two words each ("Soil", "Pattern"). A reader who has not read Methodology has
 * no way to learn what the colours on screen actually mean, and the legend under the chips
 * names the classes without saying what being in one *is*. This is that sentence, where it
 * is needed: on the chip itself, one card per chip, a title, one plain line about the
 * reading, and one line per class beside its own swatch.
 *
 * It wraps the chip rather than living beside it, so the card is positioned against the
 * button it belongs to (`position: relative` wrapper, `absolute` card below). The dock is
 * 450 px wide and the card is up to 300, so the side has to be chosen: a chip whose own
 * centre sits in the right half of the chip row anchors its card to its right edge
 * instead, and the card can never run off the panel.
 *
 * **Measured on open, not counted from the chip's index.** The chips wrap onto two or three
 * rows, so the fourth of six chips can perfectly well be the left-most thing on screen —
 * which is exactly the card that ran off the left edge when the side was picked from the
 * index. `align` stays as the first guess (and the whole answer in jsdom, where every
 * rect is zero); the first hover replaces it with where the chip actually is.
 *
 * `pointer-events: none` on the card is deliberate — it is a tooltip, not a popover with
 * anything to click, and a card that swallowed the pointer would flicker the chip under it.
 */
import {
  cloneElement,
  useCallback,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
} from 'react';

/** One class row: the chip's own swatch colour (or the hatch) and its plain sentence. */
export interface ChipHelpClass {
  id: string;
  /** The class's colour. `null` for the hatched "not assessed" row, which has none. */
  color: string | null;
  label: string;
}

export interface ChipHelpProps {
  /** The tooltip's own id — the chip points at it with `aria-describedby` while open. */
  helpId: string;
  title: string;
  /** One plain sentence about what the chip reads. */
  what: string;
  classes: ChipHelpClass[];
  /** The side to anchor on before the first hover measures the chip's real position. */
  align?: 'left' | 'right';
  /** The chip itself. */
  children: ReactElement<HTMLAttributes<HTMLElement>>;
}

export function ChipHelp({
  helpId,
  title,
  what,
  classes,
  align = 'left',
  children,
}: ChipHelpProps) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<'left' | 'right'>(align);
  const wrap = useRef<HTMLSpanElement>(null);

  /** Anchor on whichever side keeps the card inside the chip row's own box. */
  const show = useCallback(() => {
    const node = wrap.current;
    const row = node?.closest('.uc1-chips') ?? node?.parentElement ?? null;
    if (node && row) {
      const chip = node.getBoundingClientRect();
      const box = row.getBoundingClientRect();
      // Zero-width boxes are jsdom, not a layout — leave the caller's guess alone there.
      if (box.width > 0) setSide(chip.left + chip.width / 2 > box.left + box.width / 2 ? 'right' : 'left');
    }
    setOpen(true);
  }, []);

  return (
    <span
      ref={wrap}
      className="chiphelp-wrap"
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
    >
      {cloneElement(children, {
        'aria-describedby': open ? helpId : children.props['aria-describedby'],
        onFocus: show,
        onBlur: () => setOpen(false),
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Escape') setOpen(false);
        },
      })}
      {open ? (
        <span
          id={helpId}
          role="tooltip"
          className={side === 'right' ? 'chiphelp chiphelp-right' : 'chiphelp'}
        >
          <span className="chiphelp-title">{title}</span>
          <span className="chiphelp-what">{what}</span>
          <span className="chiphelp-classes">
            {classes.map((c) => (
              <span className="chiphelp-row" key={c.id}>
                <span
                  className={c.color ? 'uc1-swatch' : 'uc1-swatch uc1-swatch-hatched'}
                  style={c.color ? { background: c.color } : undefined}
                  aria-hidden="true"
                />
                <span>{c.label}</span>
              </span>
            ))}
          </span>
        </span>
      ) : null}
    </span>
  );
}

export default ChipHelp;
